
import { ChevronLeft, ChevronRight, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MainNavigation from './MainNavigation';
import SubNavigation from './SubNavigation';
import { mainNavItems, subNavItems } from './navigationConfig';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { logout, setLogoutLoading } from '@/store/slices/authSlice';
import { toast } from '@/components/ui/use-toast';
import { CopilotSidebarButton } from '../copilot/CopilotSidebarButton';
// Use js-cookie to clear cookies
import Cookies from 'js-cookie';
import { maybeFetchWorkspaceSettings } from '@/store/slices/workspaceSettings/workspaceSettingsSlice';
import { useSelector } from 'react-redux';
import { getUserId } from '@/utils/helpers/helpers';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectAllTeams } from '@/store/slices/teams/selectors';
import { RootState } from '@/store/store';
import { fetchTeams } from '@/store/slices/teams/teamsSlice';
import { useAuth } from '@/context/AuthContext';

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { sessionSignOut } = useAuth();
  const { settings } = useSelector((state: RootState) => state.workspaceSettings);
  const teams = useAppSelector(selectAllTeams);
  const currentUserId = getUserId();
  const teamMappedByIdsWithUserIds = teams.map((team) => ({ id: team.id, userIds: team.teamMembers.map((member) => member.id) }));
  const userTeamId = teamMappedByIdsWithUserIds.find((team) => team.userIds.includes(currentUserId))?.id;
  const doesUserHaveAccessToCopilot = settings?.copilotsEnabled && !settings?.excludedTeamsFromCopilot?.includes(userTeamId) && userTeamId;

  // Get the current route segment to determine active nav
  const currentRoute = location.pathname.split('/');
  const initialMainNav = currentRoute[1] === 'inbox' ? 'inbox' :
    (currentRoute[1] === 'home/inbox' && currentRoute[2] ? currentRoute[2] : 'home');

  const [activeMainNav, setActiveMainNav] = useState(initialMainNav);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [isSecondPanelCollapsed, setIsSecondPanelCollapsed] = useState(false);

  // Update activeMainNav when route changes
  useEffect(() => {
    const currentRoute = location.pathname.split('/');
    if (currentRoute[1] === 'inbox') {
      setActiveMainNav('inbox');
    } else if (currentRoute[1] === 'home' && currentRoute[2]) {
      setActiveMainNav(currentRoute[2]);
    } else if (currentRoute[1] === 'home') {
      setActiveMainNav('home');
    }
  }, [location]);

  useEffect(() => {
    dispatch(maybeFetchWorkspaceSettings() as any);
    dispatch(fetchTeams() as any);
  }, [dispatch]);

  const handleLogout = async () => {
    console.log("🚪 Starting logout process...");

    try {
      // Set logout loading state to prevent re-renders
      dispatch(setLogoutLoading(true));

      // Clear Redux state FIRST
      dispatch(logout());
      console.log("✅ Redux state cleared");

      // Clear all storage
      localStorage.clear();
      sessionStorage.clear();
      Cookies.remove('courier_token');

      // Clear cookies
      document.cookie = "customerToken=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
      document.cookie = "agent_email=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
      document.cookie = "workspaceId=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";

      console.log("✅ Storage cleared");

      // Auth signout LAST
      try {
        await sessionSignOut();
        console.log("✅ Auth signout completed");
      } catch (error) {
        console.warn("⚠️ Auth signout failed, continuing:", error);
      }

      // Reset logout loading state
      dispatch(setLogoutLoading(false));

      // Redirect to sign-in page
      navigate('/');

    } catch (error) {
      console.error("❌ Error during logout:", error);
      // Reset loading state even on error
      dispatch(setLogoutLoading(false));
      toast({
        title: "Logout Error",
        description: "There was an error during logout. Please try again.",
        variant: "destructive"
      });
    }
  };

  const toggleExpanded = (itemTitle: string) => {
    setExpandedItems(prev =>
      prev.includes(itemTitle)
        ? prev.filter(item => item !== itemTitle)
        : [...prev, itemTitle]
    );
  };

  const toggleSecondPanel = () => {
    setIsSecondPanelCollapsed(prev => !prev);
  };

  // Show secondary nav only for non-home items that have sub-items
  const shouldShowSecondaryNav = activeMainNav !== 'home' && subNavItems[activeMainNav as keyof typeof subNavItems];

  return (
    <>
      <div className="w-16 min-h-screen relative">
        {/* Background with layered effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50/95 via-white/90 to-slate-100/85 backdrop-blur-3xl" />
        <div className="absolute inset-0 bg-gradient-to-b from-purple-500/5 via-transparent to-indigo-500/5" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-purple-100/20 via-transparent to-transparent" />

        {/* Border with gradient */}
        <div className="absolute right-0 top-0 bottom-0 w-px bg-gradient-to-b from-purple-200/60 via-slate-200/40 to-purple-200/60" />

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col items-center justify-between py-8 px-2">
          <div className="flex flex-col items-center gap-12">
            {/* Logo with enhanced effects */}
            <div className="relative group cursor-pointer">
              {/* Animated background glow */}
              <div className="absolute -inset-4 bg-gradient-to-r from-purple-600/20 via-indigo-500/20 to-blue-500/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-all duration-700 animate-pulse" />
              <div className="absolute -inset-2 bg-gradient-to-br from-purple-400/30 to-blue-400/20 rounded-2xl blur-xl opacity-40 group-hover:opacity-70 transition-all duration-500" />

              {/* Logo container */}
              <div className="relative bg-white/90 backdrop-blur-md rounded-2xl p-3 shadow-lg shadow-purple-500/10 group-hover:shadow-2xl group-hover:shadow-purple-500/25 transition-all duration-500 border border-white/60 group-hover:border-purple-200/70 ring-1 ring-purple-100/20 group-hover:ring-purple-200/40">
                <img
                  src="https://framerusercontent.com/images/9N8Z1vTRbJsHlrIuTjm6Ajga4dI.png"
                  alt="Logo"
                  className="w-8 h-8 object-contain transition-all duration-500 group-hover:scale-110 relative z-10 filter drop-shadow-sm group-hover:drop-shadow-md"
                />

                {/* Shine effect */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
            </div>

            <MainNavigation
              activeMainNav={activeMainNav}
              setActiveMainNav={setActiveMainNav}
              navigate={navigate}
            />
          </div>

          {/* Bottom section */}
          <div className="flex flex-col gap-6">
            {/* Copilot button with enhanced container */}
            {
              doesUserHaveAccessToCopilot && (
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl blur-md opacity-0 hover:opacity-100 transition-opacity duration-300" />
                  <CopilotSidebarButton />
                </div>
              )
            }

            {/* Logout button with premium styling */}
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-red-500/20 to-pink-500/20 rounded-xl blur-md opacity-0 group-hover:opacity-100 transition-all duration-500" />
              <Button
                variant="ghost"
                size="icon"
                className="relative w-12 h-12 rounded-xl text-red-500 hover:text-red-600 bg-white/60 hover:bg-gradient-to-br hover:from-red-50/95 hover:to-red-100/80 backdrop-blur-sm flex justify-center items-center transition-all duration-500 hover:shadow-xl hover:shadow-red-500/20 border border-red-100/40 hover:border-red-200/60 ring-1 ring-red-100/20 hover:ring-red-200/40 group"
                onClick={() => handleLogout()}
              >
                {/* Background glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-red-100/50 to-red-200/40 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-all duration-500 scale-90 group-hover:scale-100" />

                {/* Icon with enhanced animation */}
                <LogOut className="h-5 w-5 relative z-10 transition-all duration-300 group-hover:scale-110 group-hover:rotate-12" />

                {/* Shine effect */}
                <div className="absolute inset-0 rounded-xl bg-gradient-to-tr from-transparent via-white/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                <span className="sr-only">Logout</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Ambient light effect */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-32 h-32 bg-gradient-to-r from-purple-400/10 to-indigo-400/10 rounded-full blur-3xl opacity-60 animate-pulse" />
      </div>

      {shouldShowSecondaryNav && (
        <SubNavigation
          activeMainNav={activeMainNav}
          isSecondPanelCollapsed={isSecondPanelCollapsed}
          toggleSecondPanel={toggleSecondPanel}
          expandedItems={expandedItems}
          toggleExpanded={toggleExpanded}
          navigate={navigate}
        />
      )}
    </>
  );
};

export default Sidebar;
